import {BufferReaderType} from "_src/client/buffer_reader";
import {mat4, quat, vec3, vec4} from "_src/libs/gl-matrix";
import windowManager from "_src/client/window_manager";
import {screenUtils} from "_src/client/utils";
import intersectEvaluator from "_src/engine/intersect_evaluator";
import events from "_src/engine/events";
import workspace from "_src/workspace";

const _annotations = [];
const _vec3Tmp = vec3.create();
const _vec4Tmp = vec4.create();
const _mat4Tmp = mat4.create();

export default {
  resetAnnotations(annotations) {
    _annotations.length = 0;
    for (const annotation of annotations || []) {
      const annotationWrapper = {
        "meta"      : JSON.parse(JSON.stringify(annotation)),
        "x"         : -1,
        "y"         : -1,
        "z"         : -1,
        "hidden"    : false,
        "invisible" : false,
      }

      _annotations.push(annotationWrapper);
    }
  },

  unparentAnnotations(object) {
    const annotations = _annotations.filter(annotation => annotation.meta.parentID === object.getID());
    for (const annotation of annotations) {
      const matTransform = object.model.modelMatrix;
      const quatTransform = object.model.quat;
      vec3.transformMat4(annotation.meta.cameraLocalPos, annotation.meta.cameraLocalPos, matTransform);
      quat.multiply(annotation.meta.cameraLocalQuat, quatTransform, annotation.meta.cameraLocalQuat);
      vec3.transformMat4(annotation.meta.localPoint, annotation.meta.localPoint, matTransform);
      annotation.meta.parentID = null;
    }

    if (annotations.length > 0) {
      events.emit("annotationsMetaChanges", annotations);
    }
  },

  async updateAnnotations(withOcclusionTest = true) {
    const mainWindow = windowManager.getWindow(windowManager.MAIN);
    const camera = mainWindow.renderGraph.getWorkspaceScene().camera;
    camera.updateMatrix();

    const nearPlane = vec4.set(_vec4Tmp, 0, 0, -1, 0);
    vec3.transformQuat(nearPlane, nearPlane, camera.quat);
    const origin = vec3.scaleAndAdd(_vec3Tmp, camera.trans, nearPlane, camera.near);
    nearPlane[3] = -vec3.dot(origin, nearPlane);

    const projMatrix = camera.calculatePerspectiveProjMat(_mat4Tmp);
    const viewProjMat = mat4.multiply(_mat4Tmp, projMatrix, camera.viewMat);

    for (const annotation of _annotations) {
      annotation["invisible"] = false;

      const object = workspace.getObjectByID(annotation.meta.parentID);
      calcAnnotationPosition(annotation, object, camera, nearPlane, viewProjMat);

      const ndcX = annotation["x"];
      const ndcY = annotation["y"];

      const {x, y} = screenUtils.fromScreen(mainWindow.getViewport(), camera.viewport, ndcX, -ndcY);
      const virtualPoint = screenUtils.physicalPointToVirtual([], [x, y]);

      annotation["x"] = Math.floor(virtualPoint[0]);
      annotation["y"] = Math.floor(virtualPoint[1]);

      if (annotation["invisible"]) {
        annotation["hidden"] = true;
        continue;
      }

      if (1 < ndcX || ndcX < -1 || 1 < ndcY || ndcY < -1) {
        annotation["invisible"] = true;
        continue;
      }

      if (withOcclusionTest) {

        const texCoords = screenUtils.fromScreen(mainWindow.getViewport(), camera.viewport, ndcX, ndcY);
        const textureX = mainWindow.toViewportX(Math.floor(texCoords.x));
        const textureY = mainWindow.toViewportY(Math.floor(texCoords.y));

        const depth = await mainWindow.bufferReader.readPixelCache(textureX, textureY, BufferReaderType.GENERAL_PURPOSE);

        if (depth >= 0.0) {
          const depthToEye = camera.isPerspective() ? intersectEvaluator.perspDepthToEye : intersectEvaluator.orthoDepthToEye;
          const linearZGPU = depth === 1 ? Infinity : depthToEye(depth, camera.near, camera.far);
          const linearZCPU = depthToEye(annotation["z"], camera.near, camera.far);

          const delta = Math.min(0.01 * linearZCPU, 1.0);
          annotation["hidden"] = linearZCPU > linearZGPU + delta
        }
      }
    }

    events.emit("annotationsChanges", _annotations);
  },

  async createAnnotationMeta(clientX, clientY) {
    const farPointDistance = 38440000;

    const mainWindow = windowManager.getWindow(windowManager.MAIN);
    const camera = mainWindow.renderGraph.getWorkspaceScene().camera;

    const [phyX, phyY] = screenUtils.virtualPointToPhysical([], [clientX, clientY]);
    const ndc = screenUtils.toScreen(mainWindow.getViewport(), camera.viewport, phyX, phyY);

    const {x, y} = screenUtils.fromScreen(mainWindow.getViewport(), camera.viewport, ndc.x, -ndc.y);

    const point = vec3.create();
    let res = await mainWindow.getPixelWorldPoint(Math.floor(x), Math.floor(y), camera, point);
    if (!res) {
      res = await mainWindow.getPixelWorldPoint(Math.floor(x), Math.floor(y), camera, point, true);
    }

    const parent = res ? res.object : null;
    if (!parent) {
      const origin = camera._calculateOriginPersp(vec3.create());
      const ray = camera._calculateRayPersp(ndc.x, -ndc.y, vec3.create());
      vec3.scaleAndAdd(point, origin, ray, farPointDistance);
    }

    const toLocalMat = parent ? mat4.invert(mat4.create(), parent.model.modelMatrix) : mat4.create();
    const toLocalQuat = parent ? quat.invert(quat.create(), parent.model.quat) : quat.create();

    const localTrans = vec3.transformMat4(vec3.create(), camera.trans, toLocalMat);
    const localPoint = vec3.transformMat4(vec3.create(), point, toLocalMat);
    const localQuat = quat.multiply(quat.create(), toLocalQuat, camera.quat);

    const annotationMeta = {
      parentID: parent ? parent.getID() : null,
      localPoint: localPoint,
      cameraLocalQuat: localQuat,
      cameraLocalPos: localTrans,
      cameraTop : camera.top
    };

    return annotationMeta;
  },

  getAnnotationByUUID(uuid) {
    return _annotations.find(annotation => annotation.meta.uuid === uuid);
  },
}

const calcAnnotationPosition = (function() {

  const vec3Tmp = vec3.create();
  const vec3Tmp2 = vec3.create();
  const identityMat = mat4.create();

  return function(annotation, object, camera, nearPlane, viewProjMat) {
    const isObjectAnnotation = Boolean(object);
    const transform = isObjectAnnotation ? object.model.modelMatrix : identityMat;

    const wPoint = vec3.transformMat4(vec3Tmp, annotation.meta.localPoint, transform);
    const point = isObjectAnnotation ? camera.projectPoint(wPoint[0], wPoint[1], wPoint[2], vec3Tmp2) :
                                       projectPoint(wPoint[0], wPoint[1], wPoint[2], viewProjMat, vec3Tmp2);

    annotation["x"] = point[0];
    annotation["y"] = point[1];
    annotation["z"] = 0.5 * (point[2] + 1);

    annotation["invisible"] = vec3.dot(wPoint, nearPlane) + nearPlane[3] < 0.0;
  }

})();

const projectPoint = (function() {
  const _vec4Tmp1 = vec4.create();
  return function(x, y, z, viewProjMat, dest) {
    const point = vec4.set(_vec4Tmp1, x, y, z, 1);

    vec4.transformMat4(point, point, viewProjMat);
    vec3.scale(point, point, 1.0 / point[3]);

    return vec3.copy(dest, point);
  }
})();
